
import React, { useState } from "react";
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle 
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Gift } from "lucide-react";

interface AddGiftCardDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onCreateGiftCard: (firstName: string, lastName: string, classes: number) => void;
  isCreating?: boolean;
}

const AddGiftCardDialog: React.FC<AddGiftCardDialogProps> = ({
  isOpen,
  onOpenChange,
  onCreateGiftCard,
  isCreating = false
}) => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [classes, setClasses] = useState<string>("2");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onCreateGiftCard(firstName, lastName, parseInt(classes) || 1);
  };

  const handleClassesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Allow only positive numbers
    if (!value || /^[1-9]\d*$/.test(value)) {
      setClasses(value);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Gift className="h-5 w-5" />
            Create Gift Card
          </DialogTitle>
          <DialogDescription>
            Create a gift card for someone special. They'll receive a unique code to use when booking a class.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">First Name</Label>
                <Input
                  id="firstName"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="Jane"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">Last Name</Label>
                <Input
                  id="lastName"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Doe"
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="classes">Number of Classes</Label>
              <Input
                id="classes"
                type="number"
                min="1"
                value={classes}
                onChange={handleClassesChange}
                placeholder="2"
                required
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="submit"
              disabled={isCreating || !firstName || !lastName || !classes}
            >
              {isCreating ? "Creating..." : "Create Gift Card"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddGiftCardDialog;
